import {
  type Database,
  type DatabaseValue,
  type DatabaseField,
  DatabaseFieldType,
} from '@databases/types/database';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';

import {
  type DatabaseGroup,
  EMPTY_VALUE_GROUP_KEY,
} from './database-group/database-group';
import {
  createDatabaseGroup,
  generateGroupKeyForFieldValue,
} from './database-group/database-group-factory';
import { DatabaseGroupSelectField } from './database-group/database-group-select-field';
import { type StateSelectors } from './state-selectors/state-selectors';

export type GroupsByKey = Record<string, DatabaseGroup>;
export type EntriesByGroup = Record<string, Set<string>>;

type EntryGrouping = {
  groupsByKey: GroupsByKey;
  entriesByGroup: EntriesByGroup;
};

/** Assigns database entries into groups by their value on a given field. */
export function groupEntriesByField(
  database: Database,
  entryIds: string[],
  groupField: DatabaseField,
  stateSelectors: StateSelectors
): EntryGrouping {
  const groupsByKey: Record<string, DatabaseGroup> = {};
  const entriesByGroup: Record<string, Set<string>> = {};

  // For select fields, always include a group for every option
  if (groupField.type === DatabaseFieldType.SELECT) {
    groupField.settings.selectOptions.forEach((option) => {
      const group = DatabaseGroupSelectField.createFromSelectOption(
        option,
        groupField as DatabaseField<DatabaseFieldType.SELECT>
      );
      groupsByKey[group.key] = group;
      entriesByGroup[group.key] = new Set();
    });
  }

  // Determine group key for each entry, and assign the entry to the appropriate group
  entryIds.forEach((entryId) => {
    const [groupKey, groupValue] = getGroupKeyAndValueForEntry(
      database,
      entryId,
      groupField,
      stateSelectors
    );

    // Create group if it doesn't exist yet
    if (!groupsByKey[groupKey]) {
      groupsByKey[groupKey] = createDatabaseGroup(
        groupKey,
        stateSelectors,
        groupField,
        groupValue,
        entryId
      );
      entriesByGroup[groupKey] = new Set();
    }

    entriesByGroup[groupKey].add(entryId);
  });

  return {
    groupsByKey,
    entriesByGroup,
  };
}

/**
 * Returns the group key and value for a given entry in the database.
 * If the entry's value is empty, it will be assigned to the empty value group.
 */
function getGroupKeyAndValueForEntry(
  database: Database,
  entryId: string,
  groupField: DatabaseField,
  stateSelectors: StateSelectors
): [string, DatabaseValue | undefined] {
  const entry = getDatabaseEntryRequired(database, entryId);
  const entryValue = stateSelectors.getConvertedDatabaseValue(
    database,
    groupField,
    entry
  );
  let groupKey: string;
  let groupValue: DatabaseValue | undefined;

  if (
    stateSelectors.isEmptyValue({ database, field: groupField, entry, value: entryValue })
  ) {
    groupKey = EMPTY_VALUE_GROUP_KEY;
  } else {
    groupKey = generateGroupKeyForFieldValue(groupField, entryValue);
    groupValue = entryValue;
  }

  return [groupKey, groupValue];
}
